import type { HiredEquipmentEntryMethod } from '../types';
import { HIRED_EQUIPMENT_ENTRY_METHODS } from '../types';
import { methodLabel } from './buildAnchor';

export function formatInitialLastName(name?: string | null, lastName?: string | null): string {
  const first = (name || '').trim();
  const last = (lastName || '').trim();
  if (!first && !last) return '';
  if (!first) return last;
  return last ? `${first.charAt(0).toUpperCase()}. ${last}` : first;
}

/** "16 — 100 Mile" / "16" → "SA 16"; otherwise the raw label. */
export function formatHeServiceAreaLabel(raw?: string | null): string {
  const value = (raw || '').trim();
  if (!value) return '';
  const match = value.match(/^SA\s*(\d{1,3})\b/i) || value.match(/^(\d{1,3})\b/);
  if (match) return `SA ${match[1]}`;
  return value;
}

export function formatHeWorkDate(raw?: string | null): string {
  const value = (raw || '').trim();
  if (!value) return '';
  const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = ymd
    ? new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: ymd ? 'UTC' : undefined,
  }).format(date);
}

export function heVendorNameFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const setup = fields.find(
    (f) => f.field === 'SubcontractorNameCom' && !(f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (setup) return setup;
  return fields.find((f) => f.field === 'SubcontractorNameCom')?.value?.trim() || '';
}

export function heServiceAreaFromForm(
  fields: Array<{ field: string; value?: string | null }>,
): string {
  return fields.find((f) => f.field === 'ServiceArea')?.value?.trim() || '';
}

/** Setup PO first (purchase-order submissions), then any line-level PONumber. */
export function hePoNumberFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const setup = fields.find(
    (f) => f.field === 'PONumber' && !(f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (setup) return setup;
  return fields.find((f) => f.field === 'PONumber')?.value?.trim() || '';
}

/** Prefer a line work date; fall back to the setup ActualStartTime. */
export function heWorkDateFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const lineDate = fields.find(
    (f) => f.field === 'ActualStartTime' && (f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (lineDate) return lineDate;
  return fields.find((f) => f.field === 'ActualStartTime')?.value?.trim() || '';
}

export function heEntryMethodFromForm(fields: Array<{ field: string; value?: string | null }>): HiredEquipmentEntryMethod | null {
  const stored = fields.find((f) => f.field === 'EntryMethod')?.value?.trim();
  if (stored && (HIRED_EQUIPMENT_ENTRY_METHODS as readonly string[]).includes(stored)) {
    return stored as HiredEquipmentEntryMethod;
  }
  if (fields.some((f) => f.field === 'PONumber' && f.value?.trim())) return 'purchase_order';
  if (fields.some((f) => f.field === 'SubcontractorOrHERep' && f.value?.trim() === 'Subcontractor')) {
    return 'subcontractor';
  }
  return null;
}

export function heApprovalTitleParts(input: {
  vendorName?: string | null;
  submitterName?: string | null;
  submitterLastName?: string | null;
  roleName?: string | null;
  total: number;
}): { lead: string; cost: string } {
  const vendor = input.vendorName?.trim() || 'Hired Equipment';
  const person = formatInitialLastName(input.submitterName, input.submitterLastName);
  const role = input.roleName?.trim();
  const who = person ? (role ? `${person} (${role})` : person) : '';
  const cost = `$${input.total.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return { lead: who ? `${vendor} — ${who}` : vendor, cost };
}

export function formatHeApprovalTitle(input: {
  vendorName?: string | null;
  submitterName?: string | null;
  submitterLastName?: string | null;
  roleName?: string | null;
  total: number;
}): string {
  const { lead, cost } = heApprovalTitleParts(input);
  return `${lead} · ${cost}`;
}

export function formatHeApprovalSubtitle(input: {
  entryMethod?: HiredEquipmentEntryMethod | null;
  poNumber?: string | null;
  serviceAreaLabel?: string | null;
  workDate?: string | null;
}): string {
  const parts: string[] = [];
  if (input.entryMethod) parts.push(methodLabel(input.entryMethod));
  const po = input.poNumber?.trim();
  if (po) parts.push(po);
  const sa = formatHeServiceAreaLabel(input.serviceAreaLabel);
  if (sa) parts.push(sa);
  const date = formatHeWorkDate(input.workDate);
  if (date) parts.push(date);
  return parts.join(' | ');
}
